import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { adminApi } from "../../../services";
import { useCompany } from "../../../contexts/CompanyContext";
import { useColorMode } from "../../../contexts/ColorModeContext";
import { getAdminChartColors } from "./chartTheme";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

interface DailyAppointmentsData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    backgroundColor: string;
    borderColor: string;
    borderWidth?: number;
  }[];
}

interface DailyAppointmentsChartProps {
  professionalId?: number;
}

export const DailyAppointmentsChart: React.FC<DailyAppointmentsChartProps> = ({
  professionalId,
}) => {
  const { company } = useCompany();
  const { isDark } = useColorMode();
  const chartColors = getAdminChartColors(isDark);
  const navigate = useNavigate();
  const [data, setData] = useState<DailyAppointmentsData>({
    labels: [],
    datasets: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dateMap, setDateMap] = useState<Map<number, string>>(new Map());

  const goToPreviousMonth = () => {
    setSelectedDate((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() - 1);
      return newDate;
    });
  };

  const goToNextMonth = () => {
    setSelectedDate((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + 1);
      return newDate;
    });
  };

  const fetchDailyAppointments = useCallback(async () => {
    if (!company?.id) return;

    setLoading(true);
    setError(null);

    try {
      // Only pass professionalId if company is not car
      const professionalIdToUse = company?.category !== "car" ? professionalId : undefined;
      const response = await adminApi.getAppointmentsPerDay(
        selectedDate.getFullYear(),
        selectedDate.getMonth() + 1, // API expects 1-12, JavaScript uses 0-11
        professionalIdToUse
      );

      if (
        response.success &&
        response.data &&
        typeof response.data === "object"
      ) {
        const appointmentsData = response.data;

        // Extract dates and sort them chronologically by day number
        const dates = Object.keys(appointmentsData).sort((a, b) => {
          const dayA = parseInt(a.split("-")[2]); // Extract day from YYYY-MM-DD format
          const dayB = parseInt(b.split("-")[2]);
          return dayA - dayB;
        });

        // Create labels with proper day ordering (1, 2, 3... not 01, 02, 03)
        const dayLabels = dates.map((date) => {
          const day = parseInt(date.split("-")[2]); // Extract day from YYYY-MM-DD format
          return day.toString();
        });

        // Create a map from day label index to full date string
        const newDateMap = new Map<number, string>();
        dates.forEach((date, index) => {
          newDateMap.set(index, date);
        });
        setDateMap(newDateMap);

        const allServices = new Set<string>();
        Object.values(appointmentsData).forEach((dayData) => {
          Object.keys(dayData).forEach((service) => allServices.add(service));
        });

        // Define colors for different services
        const serviceColors = [
          { bg: "rgba(59, 130, 246, 0.6)", border: "rgba(59, 130, 246, 1)" },
          { bg: "rgba(16, 185, 129, 0.6)", border: "rgba(16, 185, 129, 1)" },
          { bg: "rgba(245, 158, 11, 0.6)", border: "rgba(245, 158, 11, 1)" },
          { bg: "rgba(239, 68, 68, 0.6)", border: "rgba(239, 68, 68, 1)" },
          { bg: "rgba(147, 51, 234, 0.6)", border: "rgba(147, 51, 234, 1)" },
          { bg: "rgba(236, 72, 153, 0.6)", border: "rgba(236, 72, 153, 1)" },
        ];

        // Create datasets for each service
        const datasets = Array.from(allServices).map((service, index) => {
          const colorIndex = index % serviceColors.length;
          const data = dates.map((date) => {
            const dayData = appointmentsData[date];
            return dayData[service] || 0;
          });

          return {
            label: service,
            data: data,
            backgroundColor: serviceColors[colorIndex].bg,
            borderColor: serviceColors[colorIndex].border,
            borderWidth: 1,
          };
        });

        setData({
          labels: dayLabels,
          datasets: datasets,
        });
      } else {
        throw new Error(
          response.error || "Failed to fetch appointments per day"
        );
      }
    } catch (error) {
      console.error(
        "Error fetching appointments per day, using mock data:",
        error
      );
      setError("Failed to load daily appointments data");

      // Fallback to mock data if API fails
      const daysInMonth = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth() + 1,
        0
      ).getDate();
      const dayLabels = Array.from({ length: daysInMonth }, (_, i) =>
        (i + 1).toString()
      );
      const mockData = Array.from(
        { length: daysInMonth },
        () => Math.floor(Math.random() * 15) + 2
      );

      setData({
        labels: dayLabels,
        datasets: [
          {
            label: "Agendamentos (Mock)",
            data: mockData,
            backgroundColor: "rgba(59, 130, 246, 0.6)",
            borderColor: "rgba(59, 130, 246, 1)",
            borderWidth: 1,
          },
        ],
      });
    } finally {
      setLoading(false);
    }
  }, [selectedDate, company?.id, company?.category, professionalId]);

  useEffect(() => {
    fetchDailyAppointments();
  }, [fetchDailyAppointments]);
  const chartConfig = {
    data: {
      labels: data?.labels || [],
      datasets: data?.datasets || [],
    },
    options: {
      responsive: true,
      plugins: {
        legend: {
          position: "bottom" as const,
          labels: { color: chartColors.text },
        },
        title: {
          display: true,
          padding: {
            bottom: 20,
          },
          text: `Agendamentos por Dia - ${selectedDate
            .toLocaleDateString("pt-BR", { month: "long", year: "numeric" })
            .replace(/^\w/, (c) => c.toUpperCase())}`,
          color: chartColors.title,
          font: {
            size: window.innerWidth < 640 ? 14 : 16,
          },
        },
        datalabels: {
          display: function (context: any) {
            // Only display on the last dataset (top of the stack) and on larger screens
            if (window.innerWidth < 640) return false;
            const datasetIndex = context.datasetIndex;
            const chart = context.chart;
            return datasetIndex === chart.data.datasets.length - 1;
          },
          anchor: "end" as const,
          align: "top" as const,
          formatter: function (value: any, context: any) {
            // Calculate the sum of all datasets for this data point
            const dataIndex = context.dataIndex;
            const chart = context.chart;
            const sum = chart.data.datasets.reduce(
              (total: number, dataset: any) => {
                return total + (dataset.data[dataIndex] || 0);
              },
              0
            );
            return sum > 0 ? sum.toString() : "";
          },
          color: chartColors.text,
          font: {
            weight: "bold" as const,
            size: window.innerWidth < 640 ? 10 : 12,
          },
        },
      },
      scales: {
        x: {
          stacked: true,
          grid: { color: chartColors.grid },
          title: {
            display: true,
            text: "Dia do Mês",
            color: chartColors.muted,
            font: {
              size: window.innerWidth < 640 ? 11 : 12,
            },
          },
          ticks: {
            color: chartColors.muted,
            font: {
              size: window.innerWidth < 640 ? 10 : 12,
            },
          },
        },
        y: {
          stacked: true,
          beginAtZero: true,
          grid: { color: chartColors.grid },
          ticks: {
            stepSize: 1,
            color: chartColors.muted,
            font: {
              size: window.innerWidth < 640 ? 10 : 12,
            },
          },
        },
      },
      onClick: (event: any, elements: any[]) => {
        if (elements && elements.length > 0) {
          const element = elements[0];
          const dataIndex = element.index;
          const fullDate = dateMap.get(dataIndex);
          
          if (fullDate) {
            // Set the date in sessionStorage for AdminAppointments to read
            sessionStorage.setItem("adminAppointmentsSelectedDate", fullDate);
            // Navigate to appointments page
            navigate("/admin/appointments");
          }
        }
      },
      onHover: (event: any, elements: any[]) => {
        if (event?.native?.target) {
          if (elements && elements.length > 0) {
            event.native.target.style.cursor = "pointer";
          } else {
            event.native.target.style.cursor = "default";
          }
        }
      },
    },
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl sm:rounded-2xl shadow-medium border border-gray-100 dark:border-slate-700 p-3 sm:p-4 md:p-6">
      {/* Month Navigation Header */}
      <div className="flex items-center justify-between mb-3 sm:mb-4">
        <h3 className="text-sm sm:text-base md:text-lg font-semibold text-gray-800 dark:text-slate-100 truncate flex-1 mr-2">
          {selectedDate
            .toLocaleDateString("pt-BR", { month: "long", year: "numeric" })
            .replace(/^\w/, (c) => c.toUpperCase())}
        </h3>
        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
          <button
            onClick={goToPreviousMonth}
            disabled={loading}
            className="p-1.5 sm:p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors duration-200 text-gray-600 dark:text-slate-300 hover:text-gray-800 dark:hover:text-white disabled:opacity-50"
            title="Mês anterior"
          >
            <svg
              className="w-4 h-4 sm:w-5 sm:h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <button
            onClick={goToNextMonth}
            disabled={loading}
            className="p-1.5 sm:p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors duration-200 text-gray-600 dark:text-slate-300 hover:text-gray-800 dark:hover:text-white disabled:opacity-50"
            title="Próximo mês"
          >
            <svg
              className="w-4 h-4 sm:w-5 sm:h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 sm:py-16 md:py-20">
          <div className="w-6 h-6 sm:w-8 sm:h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4">
          <p className="text-red-600 text-xs sm:text-sm">{error}</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Bar data={chartConfig.data} options={chartConfig.options} />
        </div>
      )}
    </div>
  );
};
